import { Chip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { fade } from "@material-ui/core/styles/colorManipulator";
import TextField from "@material-ui/core/TextField";
import { Clear as ClearIcon } from "@material-ui/icons";
import Debounce, { DebounceProps } from "@saleor/components/Debounce";
import { FetchMoreProps } from "@saleor/types";
import Downshift, { ControllerStateAndHelpers } from "downshift";
import { filter } from "fuzzaldrin";
import React, { useEffect, useState } from "react";

import { MultiChipsChoiceType } from "../MultiChipsSelectField";
import MultiAutocompleteChipFieldContent from "./MultiAutocompleteChipFieldContent";

const useStyles = makeStyles(
  theme => ({
    chip: {
      backgroundColor: "#accaff",
      borderRadius: "6px",
      color: "#0B337B",
      marginRight: "5px",
      marginTop: "8px",
      ...{
        "& svg": {
          backgroundColor: "#accaff",
          color: "#0B337B",
          transform: "scale(0.8)"
        }
      }
    },
    chipClose: {
      "&:hover": {
        "& span": {
          color: "#fff"
        },
        backgroundColor: "#285BAD"
      },
      height: 32,
      padding: 0,
      width: 32
    },
    chipContainer: {
      display: "flex",
      flexDirection: "column",
      marginTop: theme.spacing(1)
    },
    chipInner: {
      "& svg": {
        color: theme.palette.primary.contrastText
      },
      alignItems: "center",
      background: fade(theme.palette.primary.main, 0.8),
      borderRadius: 18,
      color: theme.palette.primary.contrastText,
      display: "flex",
      justifyContent: "space-between",
      margin: theme.spacing(1, 0),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1)
    },
    chipLabel: {
      color: theme.palette.primary.contrastText
    },
    container: {
      flexGrow: 1,
      position: "relative",
      ...{
        "& .MuiInputBase-root": {
          display: "inline-block",
          paddingBottom: 0
        }
      },
      ...{
        "& MuiOutlinedInput-inputMultiline": {
          padding: "10px 0 0 0 !important"
        }
      }
    },
    disabledChipInner: {
      "& svg": {
        color: theme.palette.grey[200]
      },
      alignItems: "center",
      background: fade(theme.palette.grey[400], 0.8),
      borderRadius: 18,
      color: theme.palette.primary.contrastText,
      display: "flex",
      justifyContent: "space-between",
      margin: theme.spacing(1, 0),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1)
    },
    hideCloseIcon: {
      display: "none"
    },
    textfield: {
      "& .MuiInputBase-fullWidth": {
        "& textarea": {
          width: "min-content"
        },
        display: "flex",
        flexWrap: "wrap",
        paddingBottom: "10px"
      }
    }
  }),
  { name: "MultiChipsSelectField" }
);

export interface MultiAutocompleteChipFieldProps
  extends Partial<FetchMoreProps> {
  disabled?: boolean;
  displayValues: MultiChipsChoiceType[];
  error?: boolean;
  name: string;
  choices: MultiChipsChoiceType[];
  value: string[];
  limit?: number;
  loading?: boolean;
  placeholder?: string;
  helperText?: string;
  label?: string | React.ReactNode;
  hasSelectAll: boolean;
  selectAllProps: MultiChipsChoiceType;
  fetchChoices?: (value: string) => void;
  onChange: (event: React.ChangeEvent<any>) => void;
  onKeyPress: (event: React.ChangeEvent<any>) => void;
  onRemove: (event: React.ChangeEvent<any>) => void;
  onChangeText: (text: string) => void;
}

const DebounceAutocomplete: React.ComponentType<DebounceProps<
  string
>> = Debounce;

const MultiAutocompleteChipFieldComponent: React.FC<MultiAutocompleteChipFieldProps> = props => {
  const {
    disabled,
    choices,
    displayValues = [],
    error,
    hasMore,
    helperText,
    label,
    limit,
    loading,
    name,
    placeholder,
    value,
    hasSelectAll = false,
    selectAllProps,
    fetchChoices,
    onChange,
    onFetchMore,
    onKeyPress,
    onRemove,
    onChangeText,
    ...rest
  } = props;
  const classes = useStyles(props);
  const [selectAll, setSelectAll] = useState(false);

  const handleSelect = (
    item: string | [],
    downshiftOpts?: ControllerStateAndHelpers
  ) => {
    if (downshiftOpts) {
      downshiftOpts.reset({ inputValue: "" });
    }

    if (hasSelectAll && item === selectAllProps.value) {
      setSelectAll(!selectAll);
    }

    setSelectAll(false);
    onChange({
      target: { name, value: item }
    } as any);
  };

  const handleRemove = (
    item: string,
    downshiftOpts?: ControllerStateAndHelpers
  ) => {
    if (downshiftOpts) {
      downshiftOpts.reset({ inputValue: "" });
    }
    onRemove({
      target: { name, value: item }
    } as any);
  };

  const selectedValues = displayValues.map(item => item.value);

  useEffect(() => {
    if (hasSelectAll) {
      if (displayValues.length === 0) {
        setSelectAll(true);
      } else {
        setSelectAll(false);
      }
    }
  }, [displayValues]);

  return (
    <>
      <Downshift
        onInputValueChange={e => {
          fetchChoices(e);
          onChangeText(e);
        }}
        onSelect={handleSelect}
        itemToString={() => ""}
      >
        {({
          getInputProps,
          getItemProps,
          isOpen,
          toggleMenu,
          highlightedIndex,
          inputValue
        }) => (
          <div className={classes.container} {...rest}>
            <TextField
              className={classes.textfield}
              multiline
              disabled={disabled || false}
              InputProps={{
                ...getInputProps({
                  placeholder
                }),
                id: undefined,
                onClick: toggleMenu,
                startAdornment:
                  hasSelectAll && selectAll ? (
                    <Chip
                      deleteIcon={
                        <ClearIcon
                          className={disabled ? classes.hideCloseIcon : null}
                        />
                      }
                      className={classes.chip}
                      key={selectAllProps?.value || []}
                      label={selectAllProps?.label}
                      onDelete={() => handleRemove(selectAllProps?.value)}
                    />
                  ) : (
                    displayValues &&
                    displayValues.map(value => (
                      <Chip
                        deleteIcon={
                          <ClearIcon
                            className={disabled ? classes.hideCloseIcon : null}
                          />
                        }
                        className={classes.chip}
                        key={value?.value || []}
                        label={value?.label}
                        onDelete={() => handleRemove(value.value)}
                      />
                    ))
                  )
              }}
              onKeyPress={onKeyPress}
              onPaste={e => {
                e.preventDefault();
                return false;
              }}
              error={error}
              helperText={helperText}
              label={label}
              fullWidth={true}
            />
            {isOpen && !disabled && (!!inputValue || !!choices?.length) && (
              <MultiAutocompleteChipFieldContent
                choices={choices?.filter(
                  choice => !value?.includes(choice.value)
                )}
                selectedValues={selectedValues}
                getItemProps={getItemProps}
                hasMore={hasMore}
                highlightedIndex={highlightedIndex}
                loading={loading}
                limit={limit}
                inputValue={inputValue}
                onFetchMore={onFetchMore}
                hasSelectAll={hasSelectAll}
                selectedAll={selectAll}
                selectAllProps={selectAllProps}
              />
            )}
          </div>
        )}
      </Downshift>
    </>
  );
};

export interface MultiAutocompleteChipsSelectFieldProps
  extends Partial<FetchMoreProps> {
  disabled?: boolean;
  displayValues: MultiChipsChoiceType[];
  error?: boolean;
  name: string;
  choices: MultiChipsChoiceType[];
  value: string[];
  limit?: number;
  loading?: boolean;
  placeholder?: string;
  helperText?: string;
  label?: string | React.ReactNode;
  hasSelectAll: boolean;
  selectAllProps: MultiChipsChoiceType;
  fetchChoices?: (value: string) => void;
  onChange: (event: React.ChangeEvent<any>) => void;
  onKeyPress: (event: React.ChangeEvent<any>) => void;
  onRemove: (event: React.ChangeEvent<any>) => void;
  onChangeText: (text: string) => void;
}

const MultiAutocompleteChipsSelectField: React.FC<MultiAutocompleteChipsSelectFieldProps> = ({
  choices,
  fetchChoices,
  hasSelectAll,
  selectAllProps,
  ...props
}) => {
  const [query, setQuery] = React.useState("");

  if (fetchChoices) {
    return (
      <DebounceAutocomplete debounceFn={fetchChoices}>
        {debounceFn => (
          <MultiAutocompleteChipFieldComponent
            hasSelectAll={hasSelectAll}
            selectAllProps={selectAllProps}
            choices={choices}
            {...props}
            fetchChoices={debounceFn}
          />
        )}
      </DebounceAutocomplete>
    );
  }

  return (
    <MultiAutocompleteChipFieldComponent
      hasSelectAll={hasSelectAll}
      selectAllProps={selectAllProps}
      fetchChoices={q => setQuery(q || "")}
      choices={filter(choices, query, {
        key: "label"
      })}
      {...props}
    />
  );
};

MultiAutocompleteChipsSelectField.displayName =
  "MultiAutocompleteChipsSelectField";
export default MultiAutocompleteChipsSelectField;
